import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ApiError,
  getProject,
  listActivities,
  listSnags,
  listTasks,
  uploadDocument,
  type ProjectCapabilities,
  type ProjectDetail,
} from '../api.js';
import { navigate } from '../router.js';
import { sectionHref, type SectionKey } from './project-sections.js';

/**
 * The site home (W3) — the screen for somebody standing on the floor.
 *
 * `03` W3 asks for a camera-first home covering the eight things site does:
 * a photo, the diary, progress, a task, a blocker, a snag, an NCR and an
 * inspection. This is that screen, and three things shape all of it:
 *
 *   1. **One hand, one thumb.** The targets are full-width rows, stacked, with
 *      the most-used at the TOP where a thumb reaches on a large phone held
 *      low. Nothing here is a 24px link in a paragraph.
 *   2. **Minimal typing.** The camera opens on one tap and the only thing asked
 *      afterwards is what the photo is OF — chosen from a list, never typed.
 *      Everything else opens the register that owns the record, because the form
 *      there is the one that already knows the rules.
 *   3. **It never claims to work without a connection.** There is no offline
 *      store in this product, and a screen that implied one would lose
 *      somebody's morning. The indicator says whether the phone can currently
 *      reach the network and nothing more.
 *
 * WHY IT LAUNCHES RATHER THAN DUPLICATES
 * --------------------------------------
 * Every action except the photo takes you to the register that owns it. A
 * second snag form on this screen would be a second copy of the snag rules —
 * the different-person rule, the severity thresholds, the reason a send-back
 * demands — and copies drift. The photo stays here because a camera is not a
 * form; what it produces still has to land on a record, and it now does.
 *
 * EVERY PHOTO REFERS TO SOMETHING — THE USER'S RULE, 2026-08-20
 * ------------------------------------------------------------
 * *"any photo taken in site should be referring to something — for example
 * inside tasks, if it finishes so need a photo evidence."*
 *
 * This screen used to send a photo straight to the project's files, attached to
 * nothing. That was wrong twice over. It put the photo somewhere nobody looks —
 * the gallery (`Photos.tsx`) reads `FROM attachment`, so a photo attached to
 * nothing NEVER APPEARED IN IT, and the whole point of that screen is
 * "everything from Level 3 last week". And `Photos.tsx` had already argued the
 * principle in its own header: it deliberately has no upload button, because one
 * would "create exactly the untagged photo pile the per-record flow exists to
 * prevent". This screen was creating that pile through a different door.
 *
 * So the capture is still ONE TAP — the moment on site is what matters and it
 * does not wait — but the photo is held on the phone until somebody says what it
 * is of, and **nothing is uploaded until then**. Backing out saves nothing,
 * which is the honest consequence of the rule rather than a silent half-measure.
 *
 * And the send is now ONE CALL AND ONE TRANSACTION. Until 2026-08-20 it was an
 * upload followed by an attach, which could half-succeed and leave the untagged
 * photo this whole flow exists to prevent; the screen carried a sentence
 * apologising for it. The subject now rides on the upload, the server writes
 * both or neither, and the sentence is gone because the state is.
 *
 * NOT A TAB. It is reached from the project's own head, and it is a whole
 * screen rather than one of the seven sections, because a person using it is
 * not navigating a project — they are recording one thing and putting the
 * phone back in their pocket.
 */

/**
 * The URL segment: `/projects/<id>/on-site`.
 *
 * Exported so the router in `Home.tsx` and the link on the project page agree
 * on it in one place. It is deliberately NOT a `SectionKey` — adding it there
 * would put it in the tab strip, and this is not a tab.
 */
export const ON_SITE = 'on-site';

/**
 * What a tile does when it is pressed.
 *
 * `opens` is the `?new=` word the destination recognises (`useArrivedToCreate`
 * in `cross-links.tsx`) — the difference between landing on a register and
 * landing with the form already up. It is ABSENT on the two tiles that act on a
 * record somebody has to choose first: recording progress needs an activity,
 * and raising a blocker needs a task. A form opened with no subject is a form
 * that cannot be submitted, so those two land on the list, which is the flow.
 */
type Destination =
  | { readonly kind: 'camera' }
  | {
      readonly kind: 'section';
      readonly section: SectionKey;
      readonly opens?: string;
    };

interface Tile {
  readonly key: string;
  readonly label: string;
  /** What pressing it achieves, in the reader's words. Never a feature name. */
  readonly says: string;
  readonly symbol: string;
  readonly to: Destination;
  /** Absent for a role that may not do it — never shown and then refused. */
  readonly permitted: (can: ProjectCapabilities) => boolean;
}

/**
 * The eight, in the order site actually uses them.
 *
 * A photo first because it is the one people take twenty times a day, and the
 * diary second because it is the one the project is measured on when nobody
 * writes it (`06` signal 4: two working days of silence is amber).
 */
const TILES: readonly Tile[] = [
  {
    key: 'photo',
    label: 'Take a photo',
    says: 'Then say what it is of',
    symbol: '◉',
    to: { kind: 'camera' },
    permitted: (can) => can.add_document,
  },
  {
    key: 'diary',
    label: 'Write the diary',
    says: 'Weather, manpower, what happened today',
    symbol: '▤',
    to: { kind: 'section', section: 'site', opens: 'diary' },
    permitted: (can) => can.write_diary,
  },
  {
    key: 'progress',
    label: 'Record progress',
    says: 'How far along a piece of work is',
    symbol: '◑',
    to: { kind: 'section', section: 'site' },
    permitted: (can) => can.edit_activity,
  },
  {
    key: 'task',
    label: 'Add a task',
    says: 'Something that has to be done',
    symbol: '✎',
    to: { kind: 'section', section: 'site', opens: 'task' },
    permitted: () => true,
  },
  {
    key: 'blocker',
    label: 'Raise a blocker',
    says: 'Say what is stopping a task',
    symbol: '⊘',
    to: { kind: 'section', section: 'site' },
    permitted: () => true,
  },
  {
    key: 'snag',
    label: 'Raise a snag',
    says: 'Something that needs putting right',
    symbol: '△',
    to: { kind: 'section', section: 'quality', opens: 'snag' },
    permitted: (can) => can.raise_snag,
  },
  {
    key: 'ncr',
    label: 'Raise an NCR',
    says: 'Work that does not meet the spec',
    symbol: '■',
    to: { kind: 'section', section: 'quality', opens: 'ncr' },
    permitted: (can) => can.raise_ncr,
  },
  {
    key: 'inspection',
    label: 'Request an inspection',
    says: 'Ask for a walk to be booked',
    symbol: '☑',
    to: { kind: 'section', section: 'quality', opens: 'inspection' },
    permitted: (can) => can.request_inspection,
  },
];

/**
 * Where a tile goes, with its intent attached.
 *
 * `?new=` rather than a second route: the register is the same register, and
 * the word only says which form to open on arrival. It rides beside `?focus=`,
 * which the same file's `sectionHref` already builds.
 */
function destinationOf(projectId: string, to: Destination): string {
  if (to.kind === 'camera') return sectionHref(projectId, 'overview');
  const base = sectionHref(projectId, to.section);
  return to.opens ? `${base}?new=${encodeURIComponent(to.opens)}` : base;
}

/**
 * Something a photo can be OF.
 *
 * Three kinds, not the twelve the server accepts (`ATTACHABLE` in
 * `documents/service.ts`). These are the three a person standing on a floor is
 * photographing: the work they are doing, something wrong, or progress. A
 * submittal or a milestone is not photographed from site, and offering twelve
 * headings on a phone would bury the three that matter.
 *
 * `kind` is the server's own `entity_type` string, so nothing translates.
 */
interface Subject {
  readonly kind: 'task' | 'snag' | 'activity';
  readonly id: string;
  readonly label: string;
  /** The second line: where it is, who has it. Never the only way to tell two apart. */
  readonly detail: string | null;
}

const SUBJECT_GROUPS: readonly { readonly kind: Subject['kind']; readonly heading: string }[] = [
  { kind: 'task', heading: 'A task' },
  { kind: 'snag', heading: 'A snag' },
  { kind: 'activity', heading: 'Progress on some work' },
];

interface Props {
  readonly projectId: string;
}

export function SiteHome({ projectId }: Props): JSX.Element {
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** What the last photo did, so the screen can say it worked. */
  const [saved, setSaved] = useState<string | null>(null);
  const camera = useRef<HTMLInputElement>(null);

  /**
   * The photo that has been TAKEN but not yet sent, because nobody has said
   * what it is of. It lives in memory only: nothing reaches the server until a
   * subject is chosen, so backing out really does save nothing.
   */
  const [pending, setPending] = useState<File | null>(null);
  /** What it could be of. `null` while the three lists are still arriving. */
  const [subjects, setSubjects] = useState<readonly Subject[] | null>(null);

  /**
   * Load what a photo could be of, once, when one has been taken.
   *
   * EACH LIST IS ALLOWED TO FAIL ON ITS OWN, the same way the drawings picker
   * on the submittal form does. A Site Engineer sees only their OWN tasks (the
   * one scoped resource), and a role may hold `document: C` and no `snag` grant
   * at all — losing one heading is right, losing the screen is not.
   */
  const loadSubjects = useCallback(async () => {
    const found: Subject[] = [];

    try {
      // The rows only: a pick-list on the site home, not the register.
      for (const task of (await listTasks(projectId, { status: 'open' })).rows) {
        found.push({
          kind: 'task',
          id: task.id,
          label: task.title,
          detail: task.location_path ?? task.owner_name ?? null,
        });
      }
    } catch {
      /* not this role's to see */
    }

    try {
      // The rows only: this is a pick-list on the site home, not the register.
      for (const snag of (await listSnags(projectId)).rows) {
        // Finished snags are not what somebody on the floor is photographing.
        if (['verified_closed', 'accepted_as_is', 'void'].includes(snag.status)) continue;
        found.push({
          kind: 'snag',
          id: snag.id,
          label: `${snag.number} ${snag.description}`,
          detail: snag.location_path,
        });
      }
    } catch {
      /* not this role's to see */
    }

    try {
      // `lookahead: false` — the whole programme, not the next fortnight. The
      // work being photographed may have been planned months ago.
      // The rows only: a pick-list on the site home, not the register.
      for (const activity of (await listActivities(projectId, { lookahead: false })).rows) {
        found.push({
          kind: 'activity',
          id: activity.id,
          label: activity.name,
          detail: activity.location_path ?? activity.trade_name ?? null,
        });
      }
    } catch {
      /* not this role's to see */
    }

    setSubjects(found);
  }, [projectId]);

  /** Drop the held photo without sending it. */
  const discard = useCallback(() => {
    setPending(null);
    setSubjects(null);
    setError(null);
  }, []);

  /**
   * Send the held photo, attached to what it is of.
   *
   * ONE CALL, AND IT IS ONE TRANSACTION ON THE SERVER. The subject rides on the
   * upload, and the document, its version and the link commit together or not
   * at all. This used to be two calls, which left a real half-state — the bytes
   * saved and the link missing, which is a photo `Photos.tsx` cannot show and
   * nobody ever finds — and a message on this screen apologising for it. Both
   * are gone: either the photo is on the record, or it is still on the phone.
   */
  async function send(subject: Subject): Promise<void> {
    const file = pending;
    if (!file) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await uploadDocument(projectId, file, {
        category: 'photo',
        // Internal until somebody shares it, like every other file here.
        external: false,
        attachTo: { entityType: subject.kind, entityId: subject.id },
      });
      setSaved(subject.label);
      setPending(null);
      setSubjects(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'The photo did not send. It is still on your phone — try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  const load = useCallback(async () => {
    try {
      setProject(await getProject(projectId));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not open this project.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (denied) {
    return (
      <section className="site-home">
        <h2>On site</h2>
        <p className="hint">Your role does not include this project.</p>
      </section>
    );
  }

  if (!project) {
    return (
      <section className="site-home">
        <h2>On site</h2>
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Opening the project…</p>
        )}
      </section>
    );
  }

  const can = project.can;
  const tiles = TILES.filter((tile) => (can ? tile.permitted(can) : false));

  return (
    <section className="site-home">
      <div className="site-home-head">
        <h2>On site</h2>
        <p className="site-home-project">
          {project.code} · {project.name}
        </p>
      </div>

      <Reachable />

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {saved && (
        <p className="site-home-saved" role="status">
          <strong>Saved.</strong> The photo is on {saved}.
        </p>
      )}

      {/*
        WHAT IS IT OF? The one question asked after the camera, and it is
        answered by TAPPING, never by typing. It takes over the screen while it
        is up — the tiles below would be eight ways to lose the photo you are
        holding.
      */}
      {pending && (
        <div className="site-subject">
          <h3 className="site-subject-head">What is this a photo of?</h3>
          <p className="hint">
            Every site photo goes on a record, so it can be found later and so it
            counts as evidence. Nothing is sent until you choose.
          </p>

          {subjects === null ? (
            <p aria-live="polite">Finding what is open on this project…</p>
          ) : subjects.length === 0 ? (
            /*
             * An empty state has to name an action ITS reader can take. There is
             * genuinely nothing to attach to, so it names the two tiles that
             * make one — and it says the photo is still on the phone, because
             * the reader's first worry is whether they have just lost it.
             */
            <div className="empty">
              <p>
                <strong>There is nothing open to put this on yet.</strong>
              </p>
              <p>
                Raise the snag or add the task first, then photograph it. Your
                photo has not been sent and is still on your phone.
              </p>
            </div>
          ) : (
            SUBJECT_GROUPS.map(({ kind, heading }) => {
              const inGroup = subjects.filter((s) => s.kind === kind);
              if (inGroup.length === 0) return null;
              return (
                <div key={kind} className="site-subject-group">
                  <h4 className="site-subject-kind">{heading}</h4>
                  <ul className="site-subject-list">
                    {inGroup.map((subject) => (
                      <li key={`${subject.kind}-${subject.id}`}>
                        <button
                          type="button"
                          className="site-subject-pick"
                          disabled={busy}
                          onClick={() => void send(subject)}
                        >
                          <span className="site-subject-label">{subject.label}</span>
                          {subject.detail && (
                            <span className="site-subject-detail">{subject.detail}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}

          <button
            type="button"
            className="secondary site-subject-cancel"
            disabled={busy}
            onClick={discard}
          >
            Throw this photo away
          </button>
        </div>
      )}

      {tiles.length === 0 ? (
        <div className="empty">
          <p>
            <strong>Nothing here is yours to do.</strong>
          </p>
          <p>
            This screen is for recording work as it happens — photos, the diary,
            progress, snags. Your role on this project is to read it. The project’s own
            page has everything you can see.
          </p>
        </div>
      ) : (
        <ul className="site-tiles">
          {tiles.map((tile) => (
            <li key={tile.key}>
              {tile.to.kind === 'camera' ? (
                <>
                  {/*
                    A LABEL over a hidden file input, not a button that opens
                    one: `capture="environment"` asks the phone for the rear
                    camera directly, which is the difference between one tap and
                    four. On a desktop browser it degrades to a file picker,
                    which is the right thing there.
                  */}
                  <label className="site-tile" htmlFor="site-camera">
                    <span className="site-tile-symbol" aria-hidden="true">
                      {tile.symbol}
                    </span>
                    <span className="site-tile-words">
                      <span className="site-tile-label">
                        {busy ? 'Sending…' : tile.label}
                      </span>
                      <span className="site-tile-says">{tile.says}</span>
                    </span>
                  </label>
                  <input
                    id="site-camera"
                    ref={camera}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="visually-hidden"
                    disabled={busy}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      /*
                       * HELD, NOT SENT. Nothing reaches the server until the
                       * next screen says what the photo is of — see the header.
                       * The lists are fetched now rather than on mount so the
                       * eight tiles stay one request, which is what a phone on
                       * a site connection wants.
                       */
                      setError(null);
                      setSaved(null);
                      setSubjects(null);
                      setPending(file);
                      void loadSubjects();
                      // Cleared so the SAME photo can be chosen again after
                      // throwing one away.
                      if (camera.current) camera.current.value = '';
                    }}
                  />
                </>
              ) : (
                <button
                  type="button"
                  className="site-tile"
                  onClick={() => navigate(destinationOf(projectId, tile.to))}
                >
                  <span className="site-tile-symbol" aria-hidden="true">
                    {tile.symbol}
                  </span>
                  <span className="site-tile-words">
                    <span className="site-tile-label">{tile.label}</span>
                    <span className="site-tile-says">{tile.says}</span>
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="hint site-home-foot">
        <button
          type="button"
          className="link-button"
          onClick={() => navigate(`/projects/${encodeURIComponent(projectId)}`)}
        >
          Open the whole project
        </button>{' '}
        for everything else.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * Whether the phone can reach the network — and NOTHING more than that.
 *
 * `04` §mobile is explicit: there is no offline store in this product. A phone
 * that has lost signal cannot save a photo for later, and a screen that used
 * the word "offline" would tell somebody it could. So this says "no
 * connection" and tells them what that means for them right now.
 *
 * `navigator.onLine` is a weak signal — it means "there is an interface", not
 * "the server answers" — so the wording is about the phone, not the server.
 */
function Reachable(): JSX.Element | null {
  const [connected, setConnected] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const up = (): void => setConnected(true);
    const down = (): void => setConnected(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  // Silent while it is working. A permanent green badge is a badge nobody
  // reads, and this one has to be noticed the one time it matters.
  if (connected) return null;

  return (
    <p className="site-home-unreachable" role="status">
      <strong>Your phone has no connection.</strong> Nothing can be saved until it comes
      back — this app does not hold work on the phone. Keep the page open and try again
      when you have signal.
    </p>
  );
}
